import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
    Canvas,
    canvas,
    circleMarker,
    CircleMarkerOptions,
    Control,
    DomUtil,
    geoJSON,
    GeoJSONOptions,
    LatLng,
    LatLngBounds,
    latLngBounds,
    Layer,
    map,
    Map,
    polygon,
    StyleFunction,
    tileLayer,
} from 'leaflet';
import html2canvas from 'html2canvas';
import { DownloadOptions, ZipOptions, zip } from '@mapbox/shp-write';
import { Geometry, GeoJSON, GeoJsonProperties, Feature, MultiPolygon } from 'geojson';
import { DatePipe } from '@angular/common';
import { saveAs } from 'file-saver';
import { HatchImage } from './map.constants';

export interface LegendConfig {
    title: string;
    values: { color: string; value: string; size?: number }[];
}

const DEFAULT_BASEMAP_URL =
    'https://a.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png?key=cb1_2n07_1_a6331a45f16231c9a6fa1db9';

@UntilDestroy()
@Component({
    selector: 'ed-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnDestroy {
    private _layers: GeoJSON.FeatureCollection[] = [];

    @Input()
    set layers(value: GeoJSON.FeatureCollection[]) {
        this._layers = value ?? [];

        if (this._map) {
            this._refreshLayers();
        }
    }

    get layers(): GeoJSON.FeatureCollection[] {
        return this._layers;
    }

    @Input() getStyle: StyleFunction<GeoJsonProperties> = () => ({});
    @Input() getTooltip: (feature: Feature) => string | null = () => '';
    @Input() legendConfig: LegendConfig[] = [];
    @Input() title: string = '';
    @Input() mapId: string = 'mapId';
    @Input() zoomable: boolean = true;
    @Input() downloadable: boolean = true;
    @Input() baseMapUrl: string | null = null;

    public _height: string = '500px';

    @Input()
    set height(h: string | null) {
        if (h) {
            this._height = h;
        }
    }

    private _center: { lat?: number; lon?: number; zoomLevel?: number } | undefined = undefined;
    @Input()
    set center(c: { lat?: number; lon?: number; zoomLevel?: number } | undefined) {
        if (c && (c.zoomLevel || (c.lat && c.lon))) {
            this._center = c;
        }
    }

    public hatchImage = HatchImage;
    private _map!: Map;
    private _leafletLayers: Layer[] = [];
    private _legendControl?: Control;

    constructor(private readonly datePipe: DatePipe) {}

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this._plugin();
            this._initializeMap();
            this._refreshLayers();

            if (this.legendConfig.length > 0) {
                this._addLegend();
            }
        }, 0);
    }

    private _refreshLayers(): void {
        if (!this._map) {
            return;
        }

        this._clearLeafletLayers();
        this._addLayers();

        if (this._leafletLayers.length > 0) {
            this._centerMap();
        }

        this._map.invalidateSize();
    }

    private _clearLeafletLayers(): void {
        this._leafletLayers.forEach((layer) => {
            this._map.removeLayer(layer);
        });
        this._leafletLayers = [];
    }

    private _plugin(): void {
        if (Canvas) {
            Canvas.include({
                _fillStroke: function (ctx: any, layer: any) {
                    const options = layer.options;

                    if (options.fill) {
                        ctx.globalAlpha = options.fillOpacity;
                        ctx.fillStyle = options.fillColor || options.color;
                        ctx.fill(options.fillRule || 'evenodd');
                    }

                    if (options.stroke && options.weight !== 0) {
                        if (ctx.setLineDash) {
                            ctx.setLineDash((layer.options && layer.options._dashArray) || []);
                        }

                        ctx.globalAlpha = options.opacity;
                        ctx.lineWidth = options.weight;
                        ctx.strokeStyle = options.color;
                        ctx.lineCap = options.lineCap;
                        ctx.lineJoin = options.lineJoin;
                        ctx.stroke();

                        if (options.imgId) {
                            const img = document.getElementById(options.imgId);
                            ctx.save();
                            ctx.clip();
                            const bounds = layer._rawPxBounds;
                            const size = bounds.getSize();
                            const pattern = ctx.createPattern(img, 'repeat');
                            ctx.fillStyle = pattern;
                            ctx.fillRect(bounds.min.x, bounds.min.y, size.x, size.y);
                            ctx.restore();
                        }
                    }
                },
            });
        }
    }

    private _initializeMap(): void {
        this._map = map(this.mapId, {
            renderer: canvas(),
            scrollWheelZoom: false,
            boxZoom: true,
            zoomControl: this.zoomable,
            zoomDelta: 0.25,
            zoomSnap: 0,
            zoom: this._center?.zoomLevel,
        });

        tileLayer(this.baseMapUrl || DEFAULT_BASEMAP_URL, {
            crossOrigin: true,
        }).addTo(this._map);

        this._map.attributionControl.setPrefix(
            '<img class="leaflet-logo" src="/assets/img/logo_header.png"/>',
        );
    }

    private _addLayers(): void {
        this.layers.forEach((layer) => {
            const options: GeoJSONOptions = {
                style: this.getStyle,
                onEachFeature: this._onEachFeature.bind(this),
            };

            const isPointsLayer = layer.features[0]?.geometry?.type === 'Point';

            if (isPointsLayer) {
                options.pointToLayer = this._pointsToCircles();
            }

            layer.features.forEach((feature) => {
                if (feature?.properties && feature.properties['isInConcession'] === false) {
                    const coordinates = (feature.geometry as MultiPolygon).coordinates.map(
                        (polygonCoords) =>
                            polygonCoords.map((pointsArray) =>
                                pointsArray.map((point) => ({
                                    lat: point[1],
                                    lng: point[0],
                                })),
                            ),
                    );

                    const hatchedPolygon = polygon(coordinates, {
                        imgId: 'hatch',
                        color: 'black',
                        weight: 1,
                    } as any);

                    hatchedPolygon.addTo(this._map);
                    this._leafletLayers.push(hatchedPolygon);
                }
            });

            const leafletLayer = geoJSON(layer, options);
            leafletLayer.addTo(this._map);
            this._leafletLayers.push(leafletLayer);
        });
    }

    private _pointsToCircles() {
        return (feature: GeoJSON.Feature, latlng: LatLng) => {
            const style = this.getStyle(feature) as CircleMarkerOptions;
            return circleMarker(latlng, style);
        };
    }

    private _centerMap(): void {
        if (this._center?.lat && this._center?.lon) {
            this._map.setView([this._center.lat, this._center.lon]);
        } else {
            let bounds: LatLngBounds;
            const boundedLayers = this._leafletLayers.filter(
                (layer: any) => typeof layer.getBounds === 'function',
            ) as any[];
            if (!boundedLayers.length) {
                return;
            }
            bounds = latLngBounds(boundedLayers.map((layer) => layer.getBounds()));
            this._map.fitBounds(bounds);
        }

        if (this._center?.zoomLevel) this._map.setZoom(this._center.zoomLevel);
        window.dispatchEvent(new Event('resize'));
    }

    private _onEachFeature(feature: GeoJSON.Feature, layer: Layer): void {
        const tooltip = this.getTooltip(feature);
        if (tooltip && tooltip != '') {
            layer.bindTooltip(tooltip, {
                permanent: !!feature.properties?.['showTooltip'],
                direction: 'center',
                sticky: true,
            });
        }
    }

    private _addLegend() {
        if (this._legendControl) {
            this._map.removeControl(this._legendControl);
        }

        const legend = new Control({ position: 'bottomleft' });

        legend.onAdd = () => {
            const div = DomUtil.create('div', 'legend');

            this.legendConfig.forEach((config) => {
                div.innerHTML += `<div class="legend-title">${config.title}</div>`;
                const maxSize = config.values.reduce(
                    (acc, value) => ((value.size || 0) > acc ? value.size || 0 : acc),
                    0,
                );

                config.values.forEach((value) => {
                    let style = `background:${value.color};`;

                    if (value.size !== undefined) {
                        style += `border-radius: 50%;width: ${value.size * 2}px;height: ${value.size * 2}px;margin: 0 ${maxSize - value.size}px;`;

                        if (value.size === 0) {
                            style += 'border: none';
                        }
                    }

                    div.innerHTML += `<div class="legend-item"><i style="${style}"></i>${value.value}</div>`;
                });
            });

            return div;
        };

        legend.addTo(this._map);
        this._legendControl = legend;

        setTimeout(() => {
            this._map.invalidateSize();
        }, 100);
    }

    public export(layerOrder?: number): void {
        const date = new Date();
        const fileName = `${this.title.replace(' ', '_')}_${this.datePipe.transform(date, "dd.MM.yyyy_HH'h'mm")}`;

        if (layerOrder !== undefined) {
            this.downloadShapeFile(layerOrder, fileName);
        } else {
            this.downloadImage(fileName);
        }
    }

    public async downloadImage(fileName: string): Promise<void> {
        const mapElement = document.getElementById(this.mapId);

        if (!mapElement) {
            return;
        }

        this._leafletLayers.forEach((l: any) => {
            if (typeof l.bringToFront === 'function') {
                l.bringToFront();
            }
        });

        const canvas = await html2canvas(mapElement, {
            useCORS: true,
            logging: true,
            allowTaint: false,
        });

        const imgData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${fileName}.png`;
        link.href = imgData;
        link.click();
    }

    public async downloadShapeFile(layerOrder: number, fileName: string): Promise<void> {
        const options: DownloadOptions & ZipOptions = {
            folder: fileName,
            filename: this.legendConfig ? this.legendConfig[layerOrder].title : 'Shapefile',
            outputType: 'blob',
            compression: 'DEFLATE',
        };
        const zipFile = await zip(this.layers[layerOrder], options);
        saveAs(zipFile as Blob, `${fileName}.zip`);
    }

    public ngOnDestroy(): void {
        if (this._map) {
            this._map.remove();
        }
    }
}
